/**
 * Keeps a portal-specific page out of the other portal.
 *
 * The monthly upload cycle has a customer side (Data Upload, Supplier Upload
 * History, the leakage split) and a supplier side (Monthly Uploads). The
 * sidebar already hides the wrong side, but a typed URL, a stale bookmark or
 * a dual-mode company mid-switch can still land a user on the other portal's
 * page. Rather than redirect (a supplier on a customer path bouncing to
 * /dashboard and back is a loop waiting to happen), we render a small card
 * that says which portal the page belongs to and offers a single link out.
 *
 * @example
 * <PortalGate allow="supplier"><MyUploads /></PortalGate>
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { usePortalMode } from '../../hooks/usePortalMode';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { ShieldAlert } from 'lucide-react';

interface PortalGateProps {
  /** Which portal the wrapped page belongs to. */
  allow: 'customer' | 'supplier';
  children: ReactNode;
}

export default function PortalGate({ allow, children }: PortalGateProps) {
  const { portalMode } = usePortalMode();
  // 'customer_manager' is a supplier company running the Customer Portal — still the supplier side.
  const side: 'customer' | 'supplier' = portalMode === 'customer' ? 'customer' : 'supplier';

  if (side === allow) return <>{children}</>;

  return (
    <div className="p-6">
      <Card className="mx-auto max-w-md shadow-sm" data-testid="portal-gate">
        <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
            <ShieldAlert className="h-5 w-5 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-foreground">
            This page is for the {allow} portal
          </p>
          <p className="max-w-sm text-sm text-muted-foreground">
            You are viewing Helix as a {side}. Switch portal, or head back to your dashboard.
          </p>
          <Button asChild variant="outline" size="sm" className="mt-2">
            <Link to="/dashboard">Go to dashboard</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
